import { ChunkMessage } from "@/constants/enums";
import { useActiveImportStream } from "@/hooks";
import { useAiImportRuntime } from "@/store/aiImportRuntime";
import { STEP_KEYS } from "@/utils/aiImport";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

/**
 * Background service component that manages SSE connections for all active AI imports.
 * This component remains mounted even when the modal is closed, ensuring imports continue running.
 *
 * @component
 */
function AIImportBackgroundService() {
  const { t } = useTranslation(["components"]);
  const { activeImports } = useAiImportRuntime();

  const getLocalizedStepContent = useCallback(
    (stepIndex: number, messageType: ChunkMessage): string => {
      const stepKey = STEP_KEYS[stepIndex];
      if (!stepKey) return "";
      return t(`aiimportloader.steps.${stepKey}.${messageType}`);
    },
    [t]
  );

  // Render a connection manager for each active import
  // These remain active even when the modal is closed
  return (
    <>
      {Object.keys(activeImports).map((idStr) => {
        const aiImportId = Number(idStr);
        return (
          <ActiveImportConnection
            key={aiImportId}
            aiImportId={aiImportId}
            getLocalizedStepContent={getLocalizedStepContent}
          />
        );
      })}
    </>
  );
}

/**
 * Individual SSE connection manager for a single AI import.
 * Separated into its own component to prevent unnecessary re-renders.
 */
function ActiveImportConnection({
  aiImportId,
  getLocalizedStepContent,
}: {
  aiImportId: number;
  getLocalizedStepContent: (_stepIndex: number, _messageType: ChunkMessage) => string;
}) {
  // This hook manages the SSE connection and updates the runtime store
  useActiveImportStream(aiImportId, getLocalizedStepContent);

  // This is a headless component - it doesn't render anything
  return null;
}

export default AIImportBackgroundService;
